import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PendingVerificationAPI from "../usermgmt/services/PendingVerificationAPI";
import "./css/Consent.css";

/**
 * Component for handling email change verification
 * This component is used when users click on the verification link in their email
 * after requesting an email address change
 */
function VerifyEmailChange() {
  console.log("[VerifyEmailChange] Component initializing");

  const { token } = useParams();
  const [status, setStatus] = useState({
    isLoading: true,
    isVerified: false,
    message: "Verifying your email change request...",
    error: "",
  });
  const navigate = useNavigate();

  console.log(
    "[VerifyEmailChange] Component state initialized, token from URL params:",
    token ? `${token.substring(0, 10)}...` : "null"
  );

  useEffect(() => {
    const verifyEmailChange = async () => {
      console.log(
        "[VerifyEmailChange] Component mounted, starting verification process"
      );
      console.log(
        "[VerifyEmailChange] Current URL token:",
        token ? `${token.substring(0, 10)}...` : "null"
      );

      // If no token was provided in the URL, show an error
      if (!token) {
        console.warn("[VerifyEmailChange] No token provided in URL parameters");
        setStatus({
          isLoading: false,
          isVerified: false,
          message: "",
          error:
            "No verification token was provided. Please check your email and try again.",
        });
        return;
      }

      console.log(
        `[VerifyEmailChange] Token validation passed, attempting to verify token: ${token.substring(
          0,
          10
        )}...`
      );
      console.log(`[VerifyEmailChange] Full token length: ${token.length}`);
      try {
        console.log(
          "[VerifyEmailChange] Making API call to verifyEmailChange..."
        );
        console.log(
          "[VerifyEmailChange] API endpoint: /api/v1/base/users/verify-email-change"
        );
        console.log("[VerifyEmailChange] Request payload:", {
          verificationToken: token,
        });

        // Call the API to verify the email change token
        const response = await PendingVerificationAPI.verifyEmailChange(token);

        console.log("[VerifyEmailChange] API call completed successfully");
        console.log("[VerifyEmailChange] Raw response received:", response);
        console.log("[VerifyEmailChange] Response type:", typeof response);
        console.log(
          "[VerifyEmailChange] Response keys:",
          response ? Object.keys(response) : "null"
        );

        // Check if verification was successful
        // Backend returns JSON with success field
        if (response && response.success) {
          console.log("[VerifyEmailChange] Verification SUCCESS detected");
          console.log("[VerifyEmailChange] Success message:", response.message);

          setStatus({
            isLoading: false,
            isVerified: true,
            message:
              response.message ||
              "Your email address has been changed successfully. You can now use your new email address to log in.",
            error: "",
          });

          console.log(
            "[VerifyEmailChange] Status updated to success, setting 3-second redirect timer"
          );
          // Redirect to home after verification
          setTimeout(() => {
            console.log("[VerifyEmailChange] Redirecting to home page");
            navigate("/");
          }, 3000);
        } else {
          // Verification failed - handle as error
          console.warn("[VerifyEmailChange] Verification FAILED");
          console.warn(
            "[VerifyEmailChange] Response success field:",
            response?.success
          );
          console.warn(
            "[VerifyEmailChange] Full response for debugging:",
            JSON.stringify(response, null, 2)
          );

          let errorMessage =
            response?.message ||
            "Email change verification failed. The token may be invalid, expired, or already used.";

          console.warn(
            "[VerifyEmailChange] Error message to display:",
            errorMessage
          );

          setStatus({
            isLoading: false,
            isVerified: false,
            message: "",
            error: errorMessage,
          });
        }
      } catch (error) {
        console.error(
          "[VerifyEmailChange] Exception caught during verification process"
        );
        console.error(
          "[VerifyEmailChange] Error type:",
          error.constructor.name
        );
        console.error("[VerifyEmailChange] Error message:", error.message);
        console.error("[VerifyEmailChange] Full error object:", error);

        let errorMessage =
          "Failed to verify your email change request. The verification token may be invalid or expired.";

        // Check if the error has a response with data or message
        if (error.response) {
          console.error("[VerifyEmailChange] HTTP error response detected");
          console.error(
            "[VerifyEmailChange] Response status:",
            error.response.status
          );
          console.error(
            "[VerifyEmailChange] Response data:",
            error.response.data
          );

          if (error.response.data) {
            if (
              typeof error.response.data === "object" &&
              error.response.data.message
            ) {
              errorMessage = error.response.data.message;
              console.log(
                "[VerifyEmailChange] Using error message from response.data.message:",
                errorMessage
              );
            } else if (typeof error.response.data === "string") {
              errorMessage = error.response.data;
              console.log(
                "[VerifyEmailChange] Using error message from response.data string:",
                errorMessage
              );
            }
          }
        } else if (error.message) {
          errorMessage = error.message;
          console.log(
            "[VerifyEmailChange] Using error message from error.message:",
            errorMessage
          );
        }

        console.log(
          "[VerifyEmailChange] Final error message to display:",
          errorMessage
        );

        setStatus({
          isLoading: false,
          isVerified: false,
          message: "",
          error: errorMessage,
        });

        console.log("[VerifyEmailChange] Status updated to error state");
      }
    };

    console.log(
      "[VerifyEmailChange] useEffect triggered, calling verifyEmailChange function"
    );
    verifyEmailChange();
  }, [token, navigate]);

  // Handle retry button click
  const handleRetry = () => {
    console.log("[VerifyEmailChange] Retry button clicked");
    console.log("[VerifyEmailChange] Resetting status to loading state");

    setStatus({
      isLoading: true,
      isVerified: false,
      message: "Verifying your email change request...",
      error: "",
    });

    // Retry the verification after a short delay
    setTimeout(() => {
      console.log("[VerifyEmailChange] Reloading page for retry");
      window.location.reload();
    }, 500);
  };

  // Handle go home button click
  const handleGoHome = () => {
    console.log("[VerifyEmailChange] Go Home button clicked, navigating to /");
    navigate("/");
  };

  // Handle login click
  const handleLogin = () => {
    console.log(
      "[VerifyEmailChange] Login button clicked, navigating to /login"
    );
    // Navigate to login page
    navigate("/login");
  };

  return (
    <div className="verify-consent-container">
      <div className="verify-consent-card">
        <h1>Email Change Verification</h1>
        {status.isLoading && (
          <div className="verify-message loading-message">
            <p>{status.message}</p>
          </div>
        )}
        {!status.isLoading && status.isVerified && (
          <div className="verify-message success-message">
            <div className="checkmark-icon">✓</div>
            <p>{status.message}</p>
            <p>You will be redirected to the home page shortly...</p>
            <div style={{ marginTop: "20px" }}>
              <button
                className="action-button"
                onClick={handleGoHome}
                style={{ backgroundColor: "#4caf50" }}
              >
                Continue to KinShield
              </button>
            </div>
          </div>
        )}
        {!status.isLoading && !status.isVerified && (
          <div className="verify-message error-message">
            <div className="error-icon">✗</div>
            <p>{status.error}</p>
            <div style={{ marginTop: "20px" }}>
              <button className="action-button" onClick={handleRetry}>
                Try Again
              </button>
              <button
                className="action-button"
                onClick={handleLogin}
                style={{ marginLeft: "10px", backgroundColor: "#1565c0" }}
              >
                Login
              </button>
              <button
                className="action-button"
                onClick={handleGoHome}
                style={{ marginLeft: "10px", backgroundColor: "#555" }}
              >
                Go Home
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

console.log("[VerifyEmailChange] Component definition complete, exporting");

export default VerifyEmailChange;
